'use strict'

const Logger = require('@gsm/logger')
const Q = require('q')
const { resolveResourceTemplate } = require('../common/util')

const SQL = {
    SelectAccountBundlePolicies: {
        template: [
            'SELECT `policy`.`content` AS `content`, HEX(`assignment`.`entity_id`) AS `account_id`',
            'FROM `feature_policy` AS `policy`',
            'INNER JOIN `feature_bundle` AS `bundle`',
            'ON `policy`.`id` = `bundle`.`feature_policy_id`',
            'INNER JOIN `account_bundle_assignment` AS `assignment`',
            'ON `assignment`.`feature_bundle_id` = `bundle`.`id`',
            'WHERE `assignment`.`entity_id`'
        ].join(' ')
    },
    SelectGlobalPoliciesForPrincipals: {
        template: [
            'SELECT `policy`.`content` AS `content`',
            'FROM `feature_policy` AS `policy`',
            'INNER JOIN `user_role` AS `role`',
            'ON `role`.`feature_policy_id` = `policy`.`id`',
            'INNER JOIN `principal_role_assignment` AS `assignment`',
            'ON `assignment`.`user_role_id` = `role`.`id`',
            'WHERE `role`.`is_global` IS TRUE AND `assignment`.`principal`'
        ].join(' ')
    },
    SelectPoliciesForPrincipals: {
        template: [
            'SELECT `policy`.`content` AS `content`',
            'FROM `feature_policy` AS `policy`',
            'INNER JOIN `user_role` AS `role`',
            'ON `role`.`feature_policy_id` = `policy`.`id`',
            'INNER JOIN `principal_role_assignment` AS `assignment`',
            'ON `assignment`.`user_role_id` = `role`.`id`',
            'WHERE `assignment`.`principal`'
        ].join(' ')
    },
    SelectPrincipalRoleAssignments: {
        template: [
            'SELECT `policy`.`content` AS `content`, HEX(`role`.`owner_entity_id`) AS `account_id`',
            'FROM `feature_policy` AS `policy`',
            'INNER JOIN `user_role` AS `role`',
            'ON `role`.`feature_policy_id` = `policy`.`id`',
            'INNER JOIN `principal_role_assignment` AS `assignment`',
            'ON `assignment`.`user_role_id` = `role`.`id`',
            'WHERE `role`.`is_global` IS FALSE AND `assignment`.`principal`'
        ].join(' ')
    },
    SelectPolicyByIds: {
        template: [
            'SELECT `policy`.`content` AS `content`',
            'FROM `feature_policy` AS `policy`',
            'WHERE `id`'
        ].join(' ')
    }
}

const _selectInTemplate = (a) => {
    a = Array.isArray(a) ? a : []
    return `IN (${a.map(() => '?').join(',')})`
}

const _getPoliciesForPrincipals = (mysqlHelper, template, principals) => {
    const sql = [template]
    sql.push(_selectInTemplate(principals))
    return mysqlHelper.runQuery(sql.join(' '), principals)
        .then(results => {
            const parsed = []
            results.forEach(r => {
                try {
                    parsed.push(JSON.parse(r.content))
                }
                catch (error) {
                    Logger.warn('Failed parsing policy')
                    Logger.warn(error.stack)
                }
            })
            return parsed
        })
        .catch(error => {
            Logger.warn(error.stack)
            return []
        })
}

const getAccountBundlePolicies = (mysqlHelper) => {
    return (accountIds) => {
        if (!Array.isArray(accountIds) || !accountIds.length) {
            Logger.verbose('User has no account access')
            return Q([])
        }
        const sql = [SQL.SelectAccountBundlePolicies.template]
        sql.push(_selectInTemplate(accountIds))
        const binaryAccountIds = accountIds.map(id => Buffer.from(id, 'hex'))
        return mysqlHelper.runQuery(sql.join(' '), binaryAccountIds)
            .then(results => {
                const bundles = []
                results.forEach(r => {
                    const accountId = r.account_id.toLowerCase()
                    const tokens = { 'entityId': accountId }

                    try {
                        const document = JSON.parse(r.content)
                        document.Statement.forEach(s => {
                            s.Resource = resolveResourceTemplate(s.Resource, tokens)
                        })
                        bundles.push({
                            accountId,
                            document
                        })
                    }
                    catch (error) {
                        Logger.warn('Failed parsing policy')
                        Logger.warn(error.message)
                        Logger.warn(error.stack)
                    }
                })
                return bundles
            })
            .catch(error => {
                Logger.warn(error.stack)
                Logger.warn(`With SQL: ${JSON.stringify(sql)}`)
                return []
            })
    }
}

const getPrincipalRoleAssignments = (mysqlHelper) => {
    return (principals) => {
        const sql = [SQL.SelectPrincipalRoleAssignments.template]
        sql.push(_selectInTemplate(principals))
        return mysqlHelper.runQuery(sql.join(' '), principals)
            .then(results => {
                const assignments = []
                results.forEach(r => {
                    try {
                        const accountId = r.account_id.toLowerCase()
                        const document = JSON.parse(r.content)
                        assignments.push({
                            accountId,
                            document
                        })
                    }
                    catch (error) {
                        Logger.warn('Failed parsing policy')
                        Logger.warn(error.stack)
                    }
                })
                return assignments
            })
            .catch(error => {
                Logger.warn(error.stack)
                return []
            })
    }
}

const getGlobalPoliciesForPrincipals = (mysqlHelper) => {
    return (principals) => {
        const template = SQL.SelectGlobalPoliciesForPrincipals.template
        return _getPoliciesForPrincipals(mysqlHelper, template, principals)
    }
}

const getPoliciesForPrincipals = (mysqlHelper) => {
    return (principals) => {
        const template = SQL.SelectPoliciesForPrincipals.template
        return _getPoliciesForPrincipals(mysqlHelper, template, principals)
    }
}

const getPolicyByIds = mysqlHelper => {
    return (policyIds) => {
        const template = SQL.SelectPolicyByIds.template
        policyIds = Array.isArray(policyIds) ? policyIds : [policyIds]
        const binaryPolicyIds = policyIds.map(id => Buffer.from(id, 'hex'))

        return _getPoliciesForPrincipals(mysqlHelper, template, binaryPolicyIds)
    }
}

/**
 * Create DB helpers using mysqlHelper for FeaturePolicy factory.
 * @param mysqlHelper
 * @returns {{getPoliciesForAccount: function({accountId}), getPoliciesForPrincipals: function({principals}), getPolicyForRole: function({roleId})}}
 */
const getHelpers = (mysqlHelper) => {
    return {
        getAccountBundlePolicies: getAccountBundlePolicies(mysqlHelper),
        getPrincipalRoleAssignments: getPrincipalRoleAssignments(mysqlHelper),
        getGlobalPoliciesForPrincipals: getGlobalPoliciesForPrincipals(mysqlHelper),
        getPoliciesForPrincipals: getPoliciesForPrincipals(mysqlHelper),
        getPolicyByIds: getPolicyByIds(mysqlHelper)
    }
}

module.exports = {
    getHelpers
}



//////////////////
// WEBPACK FOOTER
// ./~/@gsm/authorizer/lib/feature-policy/db-helpers.js
// module id = 296
// module chunks = 51